import { useEffect, useRef } from 'react';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';
import type { Boltive, Firebase, GamTestAd, IStoreState } from '../../components/common/interfaces/types';
import { shouldLoadBoltiveTag } from '../../common/actions/boltiveActions';
import { reduxConnect } from '../../common';
import { setCookie } from '../../common/utilities/cookie';

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { firebaseConfig, boltiveConfig, gamTestAdConfig } = storeState?.appState?.appState || {};

    return {
        firebaseConfig,
        boltiveConfig,
        gamTestAdConfig
    };
};

const mapDispatchToProps = {
    shouldLoadBoltiveTag
};

type FirebaseProps = {
    firebaseConfig: Firebase;
    boltiveConfig: Boltive;
    gamTestAdConfig: GamTestAd;
    shouldLoadBoltiveTag: (enable: boolean) => void;
}

export const FirebaseWeb = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ firebaseConfig, boltiveConfig, gamTestAdConfig, shouldLoadBoltiveTag }: FirebaseProps) => {
    const isFirebaseInitialLoadComplete = useRef(false);
    const { IsEnabled: firebaseIsEnabled,
        ApiKey: firebaseApiKey,
        AuthDomain: firebaseAuthDomain,
        ProjectId: firebaseProjectId,
        StorageBucket: firebaseStorageBucket,
        MessagingSenderId: firebaseMessagingSenderId,
        AppId: firebaseAppId,
        MinimumFetchIntervalMillis: firebaseMinimumFetchIntervalMillis,
        EnableGoogleAnalytics: firebaseEnableGA4,
        GoogleAnalyticsMeasurementId: googleAnalyticsMeasurementId } = firebaseConfig || {};

    const { FirebaseRemoteConfigKey: boltiveFirebaseRemoteConfigKey } = boltiveConfig || {};
    const { GamTestAdFirebaseRemoteConfigKey: gamTestAdFirebaseRemoteConfigKey } = gamTestAdConfig || {};

    useEffect(() => {
        if (!firebaseIsEnabled) {
            return;
        }
        let firebaseApp: FirebaseApp;
        if (!isFirebaseInitialLoadComplete.current && firebaseApiKey && firebaseApiKey && firebaseAppId) {
            firebaseApp = initializeApp({
                apiKey: firebaseApiKey,
                authDomain: firebaseAuthDomain,
                projectId: firebaseProjectId,
                storageBucket: firebaseStorageBucket,
                messagingSenderId: firebaseMessagingSenderId,
                appId: firebaseAppId
            });
            isFirebaseInitialLoadComplete.current = true;
        }

        // GA4 via firebase
        try {
            if (firebaseApp && firebaseEnableGA4 && googleAnalyticsMeasurementId) {
                getAnalytics(firebaseApp);
            }
        } catch (error) {
            console.error('Failed to setup Google Analytics on Firebase', error);   
        }
        
        // firebase remote config
        try {
            if (isFirebaseInitialLoadComplete.current && firebaseApp) {
                const remoteConfig = getRemoteConfig(firebaseApp);
                remoteConfig.settings.minimumFetchIntervalMillis = firebaseMinimumFetchIntervalMillis;
                fetchAndActivate(remoteConfig).then((result) => {
                    if (result) {
                        if (gamTestAdFirebaseRemoteConfigKey) {
                            const remoteConfigGamTest = getValue(remoteConfig, gamTestAdFirebaseRemoteConfigKey);
                            if (remoteConfigGamTest) {
                                setCookie(gamTestAdFirebaseRemoteConfigKey, remoteConfigGamTest.asBoolean());
                            }
                        }
                        if (boltiveFirebaseRemoteConfigKey) {
                            const val = getValue(remoteConfig, boltiveFirebaseRemoteConfigKey); 
                            if (val) {
                                shouldLoadBoltiveTag(val.asBoolean());
                            }
                        }
                    }
                    
                }).catch(err => {
                    console.error('Fetch and Activate Firebase Remote config error', err);
                    shouldLoadBoltiveTag(false);
                });
            }
        } catch (error) {
            console.error('Firebase getRemoteConfig throttled', error);
        }
        
    }, [
        boltiveFirebaseRemoteConfigKey,
        firebaseIsEnabled,
        firebaseApiKey, 
        firebaseAppId, 
        firebaseAuthDomain,  
        firebaseMessagingSenderId, 
        firebaseMinimumFetchIntervalMillis, 
        firebaseProjectId, 
        firebaseStorageBucket,
        firebaseEnableGA4,
        googleAnalyticsMeasurementId]);

    return null;
});